import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';

import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';

import './OilMonitor.css';

import NetPosition from './NetPosition';

import Distribution from './Distribution';

import { API_BASE_URL } from '../config';

import RefreshButton from './RefreshButton';



const OiMonitor = ({ onBack }) => {

  const [chartData, setChartData] = useState([]);

  const [error, setError] = useState('');

  const [availableIndices] = useState(['NIFTY', 'BANKNIFTY', 'FINNIFTY']);

  const [activeIndex, setActiveIndex] = useState('NIFTY');

  const [positiveCount, setPositiveCount] = useState(0);

  const [negativeCount, setNegativeCount] = useState(0);

  const [loading, setLoading] = useState(false);

  const [lastUpdated, setLastUpdated] = useState(null);

  const [isUsingCachedData, setIsUsingCachedData] = useState(false);

  const [isDataStale, setIsDataStale] = useState(false);

  const [selectedTime, setSelectedTime] = useState(null);

  const [currentTime, setCurrentTime] = useState(new Date());

  const [sliderValue, setSliderValue] = useState(0);

  const [isMarketOpen, setIsMarketOpen] = useState(false);

  const [atmStrike, setAtmStrike] = useState(null);

  const [viewportWidth, setViewportWidth] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 1200));

  const latestFetchRef = useRef(0);

  const refreshIntervalRef = useRef(null);

  const mainChartRef = useRef(null);



  // Market hours: 9:15 AM to 3:30 PM (IST)

  const MARKET_START_HOUR = 9;

  const MARKET_START_MINUTE = 15;

  const MARKET_END_HOUR = 15;

  const MARKET_END_MINUTE = 30;



  // Group CE/PE by strike and compute totals; stable reference

  const mergeOIMemoized = useMemo(() => {

    return (rows = []) => {

      const toNumber = (value) => {

        if (value == null) return 0;

        if (typeof value === 'number') return Number.isFinite(value) ? value : 0;

        if (typeof value === 'string') {

          const num = parseFloat(value.replace(/,/g, ''));

          return Number.isFinite(num) ? num : 0;

        }

        const num = Number(value);

        return Number.isFinite(num) ? num : 0;

      };



      const looksLikeOptionRows = rows.some((r) => r && r.option_type);

      const looksLikeStrikeRows = !looksLikeOptionRows && rows.some((r) => r && (r.strike_price != null || r.strike != null));



      if (looksLikeStrikeRows) {

        const mapped = rows

          .map((item) => {

            const strike = toNumber(item.strike_price ?? item.strike ?? item.strikePrice);

            if (!Number.isFinite(strike) || strike === 0) return null;



            const ce = toNumber(

              item.call_oi_change_lots ??

              item.ce_oi_change_lots ??

              item.CE ??

              item.ce ??

              item.ce_change_in_oi ??

              item.ce_change_in_oi_lots ??

              item.CE_ChangeInOI ??

              item.CE_ChangeInOI_Lots

            );

            const pe = toNumber(

              item.put_oi_change_lots ??

              item.pe_oi_change_lots ??

              item.PE ??

              item.pe ??

              item.pe_change_in_oi ??

              item.pe_change_in_oi_lots ??

              item.PE_ChangeInOI ??

              item.PE_ChangeInOI_Lots

            );



            return {

              strike,

              CE: ce,

              PE: pe,

              CE_ChangeInOI: ce,

              PE_ChangeInOI: pe,

              total_ChangeInOI: ce + pe,

            };

          })

          .filter(Boolean)

          .sort((a, b) => b.strike - a.strike);



        console.log('Processed OI data:', {

          inputCount: rows.length,

          outputCount: mapped.length,

          sample: mapped.slice(0, 5),

        });

        return mapped;

      }



      const strikeMap = new Map();

      

      rows.forEach((item) => {

        try {

          const strike = Number(item.strike_price);

          if (isNaN(strike)) {

            return;

          }

          

          const typ = String(item.option_type || '').trim().toUpperCase();

          if (typ !== 'CE' && typ !== 'PE') {

            return;

          }

          

          let changeInOi = 0;

          if (item.change_in_oi != null) {

            changeInOi = typeof item.change_in_oi === 'string' 

              ? parseFloat(item.change_in_oi.replace(/,/g, '')) || 0 

              : Number(item.change_in_oi) || 0;

          }

          

          const timestamp = new Date(item.timestamp).getTime();

          if (isNaN(timestamp)) {

            return;

          }

          

          const strikeKey = strike.toString();

          const typeKey = `${strikeKey}_${typ}`;

          

          const existing = strikeMap.get(typeKey);

          if (!existing || timestamp > existing.timestamp) {

            strikeMap.set(typeKey, { strike, type: typ, changeInOi, timestamp });

          }

        } catch (error) {

          console.error('Error processing OI row:', error, item);

        }

      });

      

      const grouped = {};

      strikeMap.forEach(({ strike, type, changeInOi }) => {

        const strikeKey = strike.toString();

        

        if (!grouped[strikeKey]) {

          grouped[strikeKey] = {

            strike,

            CE: 0,

            PE: 0,

            CE_ChangeInOI: 0,

            PE_ChangeInOI: 0,

            total_ChangeInOI: 0

          };

        }

        

        if (type === 'CE') {

          grouped[strikeKey].CE = changeInOi;

          grouped[strikeKey].CE_ChangeInOI = changeInOi;

        } else if (type === 'PE') {

          grouped[strikeKey].PE = changeInOi;

          grouped[strikeKey].PE_ChangeInOI = changeInOi;

        }

        

        grouped[strikeKey].total_ChangeInOI = grouped[strikeKey].CE_ChangeInOI + grouped[strikeKey].PE_ChangeInOI;

      });

      

      const result = Object.values(grouped).sort((a, b) => b.strike - a.strike);

      

      return result;

    };

  }, [activeIndex]);



  const fetchData = useCallback(async (index, targetTime = null) => {
    if (!index) {
      setError('No index provided to fetchData');
      return;
    }

    setError('');
    setLoading(true);
    const fetchId = Date.now();
    latestFetchRef.current = fetchId;

    try {
      // Fetch OI data for the first graph with strike prices
      const oiUrl = new URL(`${API_BASE_URL}/oi`);
      oiUrl.searchParams.append('index', index);
      oiUrl.searchParams.append('_t', fetchId);

      const timeout = 300000;
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      const fetchOptions = {
        method: 'GET',
        signal: controller.signal,
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        mode: 'cors'
      };

      const response = await fetch(oiUrl, fetchOptions);
      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API Error: ${response.status} ${response.statusText}`);
      }

      const apiResponse = await response.json();
      
      if (!apiResponse.success) {
        throw new Error(apiResponse.message || 'Failed to fetch OI data');
      }

      // Handle OI data structure for strike prices
      let payload = null;
      if (apiResponse.data && typeof apiResponse.data === 'object') {
        // Check if data has nested data property
        if (apiResponse.data.data != null) {
          payload = apiResponse.data.data;
        } else {
          payload = apiResponse.data;
        }
      }
      
      let filteredData = [];
      if (Array.isArray(payload)) {
        filteredData = payload;
      } else if (payload && typeof payload === 'object') {
        const strikeRows =
          payload.strike_wise ??
          payload.strikeWise ??
          payload.strike_data ??
          payload.strikeData ??
          payload.strike_oi_change ??
          payload.strikeOiChange ??
          payload.strikes ??
          payload.option_chain ??
          payload.optionChain ??
          payload.chain ??
          payload.data?.strike_wise ??
          payload.data?.strikeWise ??
          payload.data?.strike_data ??
          payload.data?.strikeData ??
          payload.data?.strike_oi_change ??
          payload.data?.strikeOiChange ??
          payload.data?.strikes ??
          payload.data?.option_chain ??
          payload.data?.optionChain ??
          payload.data?.chain ??
          apiResponse.strike_wise ??
          apiResponse.strikeWise;

        if (Array.isArray(strikeRows)) {
          filteredData = strikeRows;
        }
      }

      if (!Array.isArray(filteredData) || filteredData.length === 0) {
        throw new Error('Strike-wise OI data not found in OI response');
      }
      
      if (targetTime && filteredData.length > 0 && filteredData[0]?.timestamp) {
        const originalCount = filteredData.length;
        const timeWindow = 5 * 60 * 1000;
        const targetTimeMs = targetTime.getTime();
        
        filteredData = filteredData.filter(item => {
          if (!item.timestamp) return false;
          
          try {
            const itemTime = new Date(item.timestamp).getTime();
            const timeDiff = Math.abs(itemTime - targetTimeMs);
            return timeDiff <= timeWindow;
          } catch (e) {
            return false;
          }
        });
        
        console.log(`Filtered ${originalCount} items to ${filteredData.length} items within time window`);
      }
      
      let mergedData = mergeOIMemoized(filteredData);

      // Extract ATM strike from OI response
      let atmValue = null;
      
      // Priority 1: Check summary.atm_strike (main location in OI API response)
      if (apiResponse?.summary?.atm_strike != null) {
        atmValue = Number(apiResponse.summary.atm_strike);
      }
      // Priority 2: Check payload.atm_strike
      else if (payload && typeof payload === 'object' && payload.atm_strike != null) {
        atmValue = Number(payload.atm_strike);
      }
      // Priority 3: Get from first data item
      else if (filteredData.length > 0 && filteredData[0]?.atm_strike) {
        atmValue = Number(filteredData[0].atm_strike);
      }
      
      // Validate ATM value
      if (!Number.isFinite(atmValue)) {
        atmValue = null;
      }
      
      console.log('ATM Strike detected from OI response:', atmValue, 'from summary:', apiResponse?.summary?.atm_strike);
      
      // Add ATM strike to chart data if it doesn't exist
      if (atmValue && mergedData.length > 0) {
        const atmIndex = mergedData.findIndex(item => Number(item.strike) === Number(atmValue));
        if (atmIndex === -1) {
          // ATM strike doesn't exist, add it
          console.log(`Adding ATM strike ${atmValue} to chart data`);
          const atmDataPoint = {
            strike: atmValue,
            CE: 0,
            PE: 0,
            CE_ChangeInOI: 0,
            PE_ChangeInOI: 0,
            total_ChangeInOI: 0,
            isATM: true
          };
          mergedData = mergedData.concat([atmDataPoint]).sort((a, b) => b.strike - a.strike);
        } else {
          // ATM strike exists, mark it
          console.log(`ATM strike ${atmValue} already exists at index ${atmIndex}`);
          mergedData[atmIndex].isATM = true;
        }
      }

      const oiCounts = mergedData.reduce((acc, row) => {
        const ce = Number(row?.CE || 0);
        const pe = Number(row?.PE || 0);
        if (ce > 0) acc.positive++;
        else if (ce < 0) acc.negative++;
        if (pe > 0) acc.positive++;
        else if (pe < 0) acc.negative++;
        return acc;
      }, { positive: 0, negative: 0 });

      if (latestFetchRef.current === fetchId) {
        setChartData(mergedData);
        setPositiveCount(oiCounts.positive);
        setNegativeCount(oiCounts.negative);
        setAtmStrike(atmValue);
        
        if (apiResponse.metadata) {
          setLastUpdated(apiResponse.metadata.last_updated);
          setIsUsingCachedData(apiResponse.metadata.is_cached || false);
          setIsDataStale(apiResponse.metadata.stale || false);
        } else {
          setLastUpdated(new Date().toISOString());
          setIsUsingCachedData(false);
          setIsDataStale(false);
        }
      }
    } catch (error) {
      console.error('Error fetching OI data:', error);
      
      if (latestFetchRef.current === fetchId) {
        if (error.name === 'AbortError') {
          setError('Request timed out. Please try again.');
        } else if (error.name === 'SyntaxError') {
          setError('Invalid response from server. Please try again later.');
        } else {
          setError(error.message || 'Failed to fetch OI data. Please try again later.');
        }
        setChartData([]);
        setPositiveCount(0);
        setNegativeCount(0);
      }
    } finally {
      if (latestFetchRef.current === fetchId) {
        setLoading(false);
      }
    }
  }, [mergeOIMemoized]);



  const formatLastUpdated = (timestamp) => {

    if (!timestamp) return 'Never';

    try {

      const date = new Date(timestamp);

      return date.toLocaleString();

    } catch (e) {

      return 'Unknown';

    }

  };



  const timeToMinutes = useCallback((hour, minute) => {

    const marketStartMinutes = MARKET_START_HOUR * 60 + MARKET_START_MINUTE;

    const timeMinutes = hour * 60 + minute;

    return timeMinutes - marketStartMinutes;

  }, []);



  const minutesToTime = useCallback((minutes) => {

    const marketStartMinutes = MARKET_START_HOUR * 60 + MARKET_START_MINUTE;

    const totalMinutes = marketStartMinutes + minutes;

    const hour = Math.floor(totalMinutes / 60);

    const minute = totalMinutes % 60;

    return { hour, minute };

  }, []);



  const checkMarketStatus = useCallback(() => {

    const now = new Date();

    const currentHour = now.getHours();

    const currentMinute = now.getMinutes();

    

    const currentMinutesFromStart = timeToMinutes(currentHour, currentMinute);

    const marketEndMinutes = timeToMinutes(MARKET_END_HOUR, MARKET_END_MINUTE);

    

    return currentMinutesFromStart >= 0 && currentMinutesFromStart <= marketEndMinutes;

  }, [timeToMinutes]);



  const getMaxSliderValue = useCallback(() => {

    const marketEndMinutes = timeToMinutes(MARKET_END_HOUR, MARKET_END_MINUTE);

    

    if (isMarketOpen) {

      const now = new Date();

      const currentMinutesFromStart = timeToMinutes(now.getHours(), now.getMinutes());

      return Math.min(currentMinutesFromStart, marketEndMinutes);

    }

    

    return marketEndMinutes;

  }, [isMarketOpen, timeToMinutes]);



  const generateTimeLabels = useCallback(() => {

    const labels = [];

    const totalMinutes = timeToMinutes(MARKET_END_HOUR, MARKET_END_MINUTE);

    const interval = Math.floor(totalMinutes / 8);

    

    for (let i = 0; i <= totalMinutes; i += interval) {

      const { hour, minute } = minutesToTime(i);

      const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;

      const percentage = (i / totalMinutes) * 100;

      labels.push({ time: timeString, percentage, minutes: i });

    }

    

    return labels;

  }, [timeToMinutes, minutesToTime]);



  const handleTimeChange = useCallback((time) => {

    setSelectedTime(time);

    console.log('Selected time:', time.toLocaleTimeString(), 'ISO:', time.toISOString());

    fetchData(activeIndex, time);

  }, [activeIndex, fetchData]);



  const handleSliderChange = useCallback((e) => {

    const value = parseInt(e.target.value);

    const maxValue = getMaxSliderValue();

    const clampedValue = Math.min(value, maxValue);

    

    setSliderValue(clampedValue);

    

    const { hour, minute } = minutesToTime(clampedValue);

    const now = new Date();

    const selectedDateTime = new Date(

      now.getFullYear(),

      now.getMonth(),

      now.getDate(),

      hour,

      minute,

      0,

      0

    );

    

    console.log('Slider changed to:', { hour, minute, selectedDateTime: selectedDateTime.toISOString() });

    handleTimeChange(selectedDateTime);

  }, [getMaxSliderValue, minutesToTime, handleTimeChange]);



  // Initial data fetch with proper interval cleanup

  useEffect(() => {

    fetchData(activeIndex);

    

    if (refreshIntervalRef.current) {

      clearInterval(refreshIntervalRef.current);

    }

    

    refreshIntervalRef.current = setInterval(() => {

      fetchData(activeIndex);

    }, 5 * 60 * 1000);

    

    return () => {

      if (refreshIntervalRef.current) {

        clearInterval(refreshIntervalRef.current);

      }

    };

  }, [activeIndex, fetchData]);



  useEffect(() => {

    if (loading) {

      document.title = `Loading OI Data - ${activeIndex} | Stocks4U`;

    } else if (error) {

      document.title = `Error - ${activeIndex} | Stocks4U`;

    } else {

      document.title = `OI Monitor - ${activeIndex} | Stocks4U`;

    }

    

    return () => {

      document.title = 'Stocks4U';

    };

  }, [loading, error, activeIndex]);



  useEffect(() => {

    let isMounted = true;

    const controller = new AbortController();



    const loadInitialData = async () => {

      try {

        const url = new URL(`${API_BASE_URL}/oi`);

        url.searchParams.append('_t', Date.now());

        const res = await fetch(url, { signal: controller.signal });

        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);

        const json = await res.json();

        if (!json.success) throw new Error(json.message || 'Failed to fetch data');



        if (isMounted && Array.isArray(json.data)) {

          const uniqueIndices = [...new Set(json.data.map((item) => item.index).filter(Boolean))];

          const indicesList = uniqueIndices.map((id) => ({

            id,

            name: id === 'NIFTY' ? 'NIFTY 50' :

                  id === 'BANKNIFTY' ? 'BANKNIFTY' :

                  id === 'FINNIFTY' ? 'FINNIFTY' :

                  id === 'MIDCAPNIFTY' ? 'NIFTY MIDCAP' :

                  id === 'SENSEX' ? 'SENSEX' : id,

          }));



          if (indicesList.length > 0 && !activeIndex) {

            const defaultIndex = indicesList[0].id;

            setActiveIndex(defaultIndex);

            await fetchData(defaultIndex);

          }

        }

      } catch (err) {

        if (err.name !== 'AbortError' && isMounted) {

          setError('Failed to fetch initial data');

        }

      }

    };



    loadInitialData();



    return () => {

      isMounted = false;

      controller.abort();

    };

  }, []);



  useEffect(() => {

    const updateTime = () => {

      const now = new Date();

      setCurrentTime(now);

      setIsMarketOpen(checkMarketStatus());

      

      if (checkMarketStatus()) {

        const currentMinutesFromStart = timeToMinutes(now.getHours(), now.getMinutes());

        setSliderValue(Math.max(0, currentMinutesFromStart));

      }

    };



    updateTime();

    const interval = setInterval(updateTime, 60000);

    

    return () => clearInterval(interval);

  }, [checkMarketStatus, timeToMinutes]);



  useEffect(() => {

    if (selectedTime) {

      const selectedDate = new Date(selectedTime);

      const selectedMinutes = timeToMinutes(selectedDate.getHours(), selectedDate.getMinutes());

      setSliderValue(Math.max(0, selectedMinutes));

    } else if (isMarketOpen) {

      const currentMinutesFromStart = timeToMinutes(currentTime.getHours(), currentTime.getMinutes());

      setSliderValue(Math.max(0, currentMinutesFromStart));

    }

  }, [selectedTime, isMarketOpen, currentTime, timeToMinutes]);



  useEffect(() => {

    const handleResize = () => setViewportWidth(window.innerWidth);

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);

  }, []);



  const xDomain = useMemo(() => {

    let minVal = 0;

    let maxVal = 0;

    

    chartData.forEach((row) => {

      const ce = Number(row?.CE || 0);

      const pe = Number(row?.PE || 0);

      minVal = Math.min(minVal, ce, pe);

      maxVal = Math.max(maxVal, ce, pe);

    });



    const padding = Math.max(Math.abs(minVal), Math.abs(maxVal)) * 0.1;

    return [minVal - padding, maxVal + padding];

  }, [chartData]);



  // Calculate dynamic height based on number of data points

  const calculateChartHeight = useMemo(() => {

    const dataCount = chartData.length;

    if (dataCount === 0) return viewportWidth <= 768 ? 520 : 1000;

    

    // ~45px per bar + spacing

    const heightPerItem = 50;

    const minHeight = viewportWidth <= 768 ? 520 : 1000;

    const maxHeight = viewportWidth <= 768 ? 2500 : 4000;

    

    const calculatedHeight = Math.max(minHeight, Math.min(dataCount * heightPerItem, maxHeight));

    return calculatedHeight;

  }, [chartData.length, viewportWidth]);



  // Adjust margins dynamically based on viewport and data

  const getChartMargins = useMemo(() => {

    const isMobile = viewportWidth <= 768;

    return {

      top: isMobile ? 10 : 15,

      right: isMobile ? 16 : 50,

      left: isMobile ? 88 : 130,

      bottom: isMobile ? 10 : 15

    };

  }, [viewportWidth]);



  if (error) {

    return (

      <div className="oil-monitor-page">

        <div className="error-container">

          <h2>Error Loading Data</h2>

          <p>{error}</p>

          <p className="error-hint">Please ensure the backend server is running and accessible at {API_BASE_URL}</p>

          <button 

            onClick={() => {

              setError('');

              fetchData(activeIndex);

            }} 

            className="retry-button"

          >

            Retry

          </button>

        </div>

      </div>

    );

  }



  return (

    <div className="oil-monitor-page oil-monitor-page-root">

      <div className="header-controls">

        <div className="s4u-page-heading">

          <button 

            onClick={onBack} 

            className="back-button s4u-back-btn"

            style={{

              padding: '8px 16px',

              backgroundColor: '#4a90e2',

              color: 'white',

              border: 'none',

              borderRadius: '4px',

              cursor: 'pointer',

              display: 'flex',

              alignItems: 'center',

              gap: '8px'

            }}

          >

            ← Back to Dashboard

          </button>

          <h2>OI Monitor</h2>

        </div>

        

        <RefreshButton 

          onRefresh={() => fetchData(activeIndex)} 

          isLoading={loading}

          size="small"

        />

        

        <div className="index-buttons">

          {['NIFTY', 'BANKNIFTY', 'FINNIFTY'].map((index) => (

            <button

              key={index}

              onClick={() => setActiveIndex(index)}

              style={{

                padding: '8px 16px',

                backgroundColor: activeIndex === index ? '#4CAF50' : '#f0f0f0',

                color: activeIndex === index ? 'white' : '#333',

                border: '1px solid #ddd',

                borderRadius: '4px',

                cursor: 'pointer',

                fontWeight: activeIndex === index ? 'bold' : 'normal'

              }}

            >

              {index}

            </button>

          ))}

        </div>

      </div>



      {isUsingCachedData && (

        <div className="alert alert-info">

          Showing cached data. Last updated: {formatLastUpdated(lastUpdated)}

        </div>

      )}

      

      {isDataStale && (

        <div className="alert alert-warning">

          Data might be stale. Last updated: {formatLastUpdated(lastUpdated)}

        </div>

      )}



      <div className="oil-monitor-layout">

        <div className="oi-clock-title-row">

          <h2 className="oi-clock-title">OI Clock ✨</h2>

        </div>



        <div

          ref={mainChartRef}

          className="oi-clock-chart-container"

          style={{ width: '100%', minHeight: viewportWidth <= 768 ? '55vh' : '70vh' }}

        >

          {loading ? (

            <div

              className="loading-container"

              style={{ minHeight: viewportWidth <= 768 ? '55vh' : '70vh' }}

            >

              <div className="loading-spinner"></div>

              <p>Loading data for {activeIndex}...</p>

            </div>

          ) : chartData.length > 0 ? (

            <div className="oi-chart" style={{ width: '100%', height: '100%' }}>

              <div className="chart-footer">

                <small>

                  Last updated: {formatLastUpdated(lastUpdated)}

                  {isUsingCachedData && ' (Cached)'}

                  {isDataStale && ' (Stale Data)'}

                </small>

              </div>

              <ResponsiveContainer

                width="100%"

                height={calculateChartHeight}

                minHeight={viewportWidth <= 768 ? 520 : 1000}

              >

                <BarChart

                  data={chartData}

                  layout="vertical"

                  barCategoryGap="20%"

                  barGap={5}

                  margin={getChartMargins}

                >

                  <XAxis

                    type="number"

                    stroke="#ccc"

                    tickFormatter={(v) => v.toLocaleString()}

                    domain={xDomain}

                    axisLine={{ stroke: '#666', strokeWidth: 1 }}

                    tickLine={{ stroke: '#666' }}

                    tick={{ fontSize: viewportWidth <= 768 ? 10 : 11, angle: -45, textAnchor: 'end' }}

                    allowDataOverflow

                  />

                  <ReferenceLine x={0} stroke="#666" strokeWidth={2} />

                  <YAxis

                    dataKey="strike"

                    type="category"

                    stroke="#ccc"

                    reversed={false}

                    interval={0}

                    width={viewportWidth <= 768 ? 88 : 130}

                    tick={{ fontSize: viewportWidth <= 768 ? 9 : 11, fill: '#ccc' }}

                    tickFormatter={(value) => value.toLocaleString()}

                    tickMargin={viewportWidth <= 768 ? 12 : 35}

                    tickLine={false}

                  />

                  <Tooltip

                    formatter={(val, name) => [Number(val).toLocaleString(), name]}

                    contentStyle={{

                      backgroundColor: '#1f2937',

                      border: '1px solid #4b5563',

                      borderRadius: '8px',

                      boxShadow: '0 10px 25px rgba(0,0,0,0.3)'

                    }}

                    labelFormatter={(label) => `Strike: ${label}`}

                  />

                  <Legend />

                          {atmStrike && (

                    <ReferenceLine

                      y={atmStrike}

                      stroke="#fbbf24"

                      strokeWidth={2}

                      strokeDasharray="5 5"

                      label={{

                        value: 'ATM',

                        position: 'right',

                        fill: '#fbbf24',

                        fontWeight: 'bold',

                        fontSize: 12,

                        offset: 10

                      }}

                    />

                  )}

                  <Bar dataKey="CE" fill="#ff2e2e" name="CE Change in OI" />

                  <Bar dataKey="PE" fill="#5bcb50" name="PE Change in OI" />

                </BarChart>

              </ResponsiveContainer>

            </div>

          ) : (

            <div className="no-data">

              <p>No OI data available. The market may be closed or there was an issue fetching data.</p>

              <button

                onClick={() => fetchData(activeIndex)}

                className="retry-button"

                disabled={loading}

              >

                {loading ? 'Loading...' : 'Retry'}

              </button>

            </div>

          )}

        </div>



        <div className="oil-monitor-bottom-grid">

          <div className="oil-monitor-panel">

            <NetPosition index={activeIndex} />

          </div>

          <div className="oil-monitor-panel">

            <Distribution index={activeIndex} />

          </div>

        </div>

      </div>

    </div>

  );

};



export default OiMonitor;

